/**
 * 날짜 및 시간 관련 유틸리티 함수
 */

/**
 * 날짜가 일요일인지 확인
 */
export const isSunday = (date) => {
  return date.getDay() === 0;
};

/**
 * 날짜가 오늘 이전인지 확인
 */
export const isPastDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
};

/**
 * 날짜가 예약 가능한지 확인 (오늘 이후 + 일요일 제외)
 */
export const isDateAvailable = (date) => {
  return !isPastDate(date) && !isSunday(date);
};

/**
 * 영업시간 내 예약 가능한 시간 슬롯 생성
 * 영업시간: 10:00 ~ 22:00
 * 시술시간: 최대 2시간
 * 마지막 시술 종료: 22:00
 */
export const getAvailableTimeSlots = () => {
  return [
    { value: '10:00', label: '10:00 - 12:00', startTime: '10:00' },
    { value: '12:00', label: '12:00 - 14:00', startTime: '12:00' },
    { value: '14:00', label: '14:00 - 16:00', startTime: '14:00' },
    { value: '16:00', label: '16:00 - 18:00', startTime: '16:00' },
    { value: '18:00', label: '18:00 - 20:00', startTime: '18:00' },
    { value: '20:00', label: '20:00 - 22:00', startTime: '20:00' },
  ];
};

/**
 * 날짜를 YYYY-MM-DD 형식으로 포맷
 */
export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 날짜를 한글 형식으로 포맷 (예: 2024년 1월 25일)
 */
export const formatDateKorean = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${year}년 ${month}월 ${day}일`;
};

/**
 * 요일을 한글로 반환
 */
export const getDayOfWeekKorean = (date) => {
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return days[date.getDay()];
};

/**
 * 날짜와 시간을 ISO 8601 형식으로 결합
 */
export const combineDateAndTime = (date, time) => {
  const formattedDate = formatDate(date);
  return `${formattedDate}T${time}`;
};

/**
 * 현재 월의 모든 날짜 배열 생성 (달력 렌더링용)
 */
export const getCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay(); // 0: 일요일, 1: 월요일, ...

  const days = [];

  // 이전 달의 빈 칸 추가
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }

  // 현재 달의 날짜 추가
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(year, month, day));
  }

  return days;
};

/**
 * 날짜가 같은지 비교
 */
export const isSameDate = (date1, date2) => {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * 월 이름을 한글로 반환
 */
export const getMonthNameKorean = (month) => {
  return `${month + 1}월`;
};

/**
 * 숫자를 한국 원화 형식으로 포맷 (천단위 구분 + '원')
 * @param {number} amount - 금액
 * @returns {string} 포맷된 금액 (예: 75,000원)
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0원';
  }
  return new Intl.NumberFormat('ko-KR').format(amount) + '원';
};

/**
 * 숫자를 천단위 구분 형식으로 포맷 ('원' 없이)
 * @param {number} number - 숫자
 * @returns {string} 포맷된 숫자 (예: 75,000)
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) {
    return '0';
  }
  return new Intl.NumberFormat('ko-KR').format(number);
};
